import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Save,
  Sun,
  Moon,
  Palette,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Shield,
} from "lucide-react";
import { changePassword } from "../../../../services/auth.service";
import {
  card,
  surfaceMuted,
  pageTitle,
  sectionTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  motionFadeUp,
  motionStaggerChild,
  motionHoverLift,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import {
  Skeleton,
  SkeletonCard,
} from "../../../StudentDashboard/Components/ui/Skeleton";

const defaultColorScheme = "green";

const schemeColors = {
  blue: "#3B82F6",
  green: "#22C55E",
  purple: "#A855F7",
  amber: "#F59E0B",
};

/* ── Toggle Switch (shared) ─────────────────────────────────── */
const ToggleSwitch = ({ enabled, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={onToggle}
    className={cn(
      "relative inline-flex h-6 w-11 items-center rounded-sm transition-colors",
      enabled ? "bg-accent1" : "bg-gray-300 dark:bg-gray-600"
    )}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-sm bg-white transition-transform",
        enabled ? "translate-x-6" : "translate-x-1"
      )}
    />
  </button>
);

/* ── Settings Skeleton ──────────────────────────────────────── */
const SettingsSkeleton = () => (
  <div className="py-6">
    <div className="max-w-4xl mx-auto px-4 space-y-6">
      {/* Title */}
      <div>
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-1 w-12 mt-3" />
        <Skeleton className="h-4 w-64 mt-2" />
      </div>
      {/* Appearance card */}
      <SkeletonCard className="p-6 space-y-6">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-1 w-12" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
        <Skeleton className="h-12 w-full" />
      </SkeletonCard>
      {/* Security card */}
      <SkeletonCard className="p-6 space-y-5">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-1 w-12" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
        <Skeleton className="h-10 w-40 ml-auto" />
      </SkeletonCard>
    </div>
  </div>
);

/* ── Main Component ─────────────────────────────────────────── */
const AccountSettings = () => {
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [isSettingsSaved, setIsSettingsSaved] = useState(false);
  const [colorScheme, setColorScheme] = useState(
    () => localStorage.getItem("colorScheme") || defaultColorScheme
  );
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  /* ── Effects ──────────────────────────────────────────────── */
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-color-scheme", colorScheme);
    localStorage.setItem("colorScheme", colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    document.body.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  /* ── Handlers ─────────────────────────────────────────────── */
  const handleThemeChange = (newTheme) => setTheme(newTheme);

  const handleColorSchemeChange = (scheme) => {
    setColorScheme(scheme);
    document.body.setAttribute("data-color-scheme", scheme);
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordMessage(null);

    if (!currentPassword || !newPassword) {
      setPasswordError("Please enter both current and new password.");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setPasswordMessage("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      setPasswordError(apiMessage || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  /* ── Skeleton state ───────────────────────────────────────── */
  if (loading) return <SettingsSkeleton />;

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <motion.div {...motionFadeUp} className="py-6">
      <div className="max-w-4xl mx-auto px-4 space-y-6">

        {/* ── Toast ───────────────────────────────────────── */}
        <AnimatePresence>
          {isSettingsSaved && (
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 40 }}
              className="fixed top-4 right-4 bg-emerald-600 text-white px-5 py-3 rounded-sm shadow-sm z-50 flex items-center gap-2"
            >
              <CheckCircle size={18} />
              <span className="text-sm font-bold">Settings saved successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Header ──────────────────────────────────────── */}
        <div>
          <h1 className={pageTitle}>Account Settings</h1>
          <div className={cn(accentBarSection, "mt-3")} />
          <p className={cn(bodySecondary, "mt-2")}>
            Customize your account preferences
          </p>
        </div>

        {/* ── Appearance Card ─────────────────────────────── */}
        <motion.div
          {...motionStaggerChild(1)}
          className={cn(card, "p-6 space-y-6")}
        >
          {/* Color Scheme */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Palette size={18} className="text-accent1" />
              <h2 className={sectionTitle}>Color Scheme</h2>
            </div>
            <div className={accentBarSection} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(schemeColors).map(([scheme, hex]) => (
                <button
                  key={scheme}
                  onClick={() => handleColorSchemeChange(scheme)}
                  className={cn(
                    "p-4 border rounded-sm flex flex-col items-center gap-2 transition-all",
                    colorScheme === scheme
                      ? "border-accent1 bg-accent1/5 shadow-sm"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  )}
                >
                  <div
                    className="w-8 h-8 rounded-sm"
                    style={{ backgroundColor: hex }}
                  />
                  <span className={microLabel}>{scheme}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Toggle */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sun size={18} className="text-accent1" />
              <h2 className={sectionTitle}>Theme</h2>
            </div>
            <div className={accentBarSection} />
            <div className={cn(surfaceMuted, "p-4 rounded-sm")}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {theme === "dark" ? (
                    <Moon size={16} className="text-gray-400" />
                  ) : (
                    <Sun size={16} className="text-gray-500" />
                  )}
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {theme === "dark" ? "Dark Mode" : "Light Mode"}
                  </span>
                </div>
                <ToggleSwitch
                  enabled={theme === "dark"}
                  onToggle={() =>
                    handleThemeChange(theme === "dark" ? "light" : "dark")
                  }
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Security Card (Change Password) ─────────────── */}
        <motion.div
          {...motionStaggerChild(2)}
          className={cn(card, "p-6 space-y-5")}
        >
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-accent1" />
            <h2 className={sectionTitle}>Change Password</h2>
          </div>
          <div className={accentBarSection} />

          {/* Error / Success Banners */}
          <AnimatePresence>
            {passwordError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400 rounded-sm"
              >
                {passwordError}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {passwordMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-400 rounded-sm"
              >
                {passwordMessage}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Password */}
            <div className="space-y-2">
              <label className={microLabel}>Current Password</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 pr-12 border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label className={microLabel}>New Password</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 pr-12 border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={motionHoverLift}
              onClick={handleChangePassword}
              disabled={isChangingPassword}
              className="px-6 py-3 bg-accent1 text-white rounded-sm shadow-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors hover:opacity-90 disabled:opacity-60"
            >
              {isChangingPassword ? "Updating..." : "Update Password"}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AccountSettings;
